import React, { useState, useEffect } from 'react';
import { Quotation, PriceScenario, PriceItem } from './types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { 
  Plus, 
  Trash, 
  Calculator,
  DollarSign,
  Users,
  Layers,
  Edit,
  Eye,
  X,
  Save,
  PenSquare,
  CreditCard
} from 'lucide-react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from '@/lib/utils';

interface PriceTabProps {
  quotation: Quotation;
  handleChange: (field: keyof Quotation, value: any) => void;
  isEditing?: boolean;
}

// Lista de moedas disponíveis
const CURRENCIES = [
  { code: 'BRL', symbol: 'R$', name: 'Real Brasileiro' },
  { code: 'USD', symbol: '$', name: 'Dólar Americano' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'Libra Esterlina' }
];

export function QuotationPriceTab({ quotation, handleChange, isEditing }: PriceTabProps) {
  // Estado para controlar os cenários de preço
  const [scenarios, setScenarios] = useState<PriceScenario[]>(quotation.priceScenarios || []);
  const [editingScenario, setEditingScenario] = useState<PriceScenario | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PriceItem | null>(null);
  const [newItem, setNewItem] = useState<Partial<PriceItem>>({
    description: '',
    unitPrice: 0,
    quantity: 1,
    total: 0
  });
  
  // Função para atualizar os cenários no estado do quotation
  const updateScenarios = (newScenarios: PriceScenario[]) => {
    setScenarios(newScenarios);
    handleChange('priceScenarios', newScenarios);
  };

  // Função para adicionar um novo cenário
  const addScenario = () => {
    const newScenario: PriceScenario = {
      id: `scenario_${Date.now()}`,
      title: `Cenário ${scenarios.length + 1}`,
      items: [],
      total: 0,
      currency: 'BRL' // Moeda padrão
    };
    updateScenarios([...scenarios, newScenario]);
  };
  
  // Função para editar um cenário
  const editScenario = (scenarioId: string) => {
    const scenario = scenarios.find(s => s.id === scenarioId);
    if (scenario) {
      setEditingScenario({...scenario});
      setIsDialogOpen(true);
    }
  };
  
  // Função para excluir um cenário
  const deleteScenario = (scenarioId: string) => {
    const updatedScenarios = scenarios.filter(s => s.id !== scenarioId);
    updateScenarios(updatedScenarios);
  };
  
  // Função para salvar as alterações em um cenário
  const saveScenario = () => {
    if (editingScenario) {
      const updatedScenarios = scenarios.map(s => 
        s.id === editingScenario.id ? editingScenario : s
      );
      updateScenarios(updatedScenarios);
      setIsDialogOpen(false);
      setEditingScenario(null);
    }
  };
  
  // Função para adicionar um novo item ao cenário
  const addItemToScenario = () => {
    if (editingScenario && newItem.description) {
      const itemTotal = (newItem.unitPrice || 0) * (newItem.quantity || 1);
      const newPriceItem: PriceItem = {
        id: `item_${Date.now()}`,
        description: newItem.description || '',
        unitPrice: newItem.unitPrice || 0,
        quantity: newItem.quantity || 1,
        total: itemTotal
      };
      
      const updatedItems = [...editingScenario.items, newPriceItem];
      const scenarioTotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
      
      setEditingScenario({
        ...editingScenario,
        items: updatedItems,
        total: scenarioTotal
      });
      
      // Resetar o formulário de novo item
      setNewItem({
        description: '',
        unitPrice: 0,
        quantity: 1,
        total: 0
      });
    }
  };
  
  // Função para remover um item do cenário
  const removeItemFromScenario = (itemId: string) => {
    if (editingScenario) {
      const updatedItems = editingScenario.items.filter(item => item.id !== itemId);
      const scenarioTotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
      
      setEditingScenario({
        ...editingScenario,
        items: updatedItems,
        total: scenarioTotal
      });
    }
  };
  
  // Função para editar um item existente
  const startEditingItem = (item: PriceItem) => {
    setEditingItem({...item});
    setNewItem({
      description: item.description,
      unitPrice: item.unitPrice,
      quantity: item.quantity,
      total: item.total
    });
  };
  
  // Função para salvar a edição de um item
  const saveItemEdit = () => {
    if (editingScenario && editingItem && newItem.description) {
      const itemTotal = (newItem.unitPrice || 0) * (newItem.quantity || 1);
      const updatedItem: PriceItem = {
        id: editingItem.id,
        description: newItem.description || '',
        unitPrice: newItem.unitPrice || 0,
        quantity: newItem.quantity || 1,
        total: itemTotal
      };
      
      const updatedItems = editingScenario.items.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      );
      
      const scenarioTotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
      
      setEditingScenario({
        ...editingScenario,
        items: updatedItems,
        total: scenarioTotal
      });
      
      // Resetar o formulário
      setEditingItem(null);
      setNewItem({
        description: '',
        unitPrice: 0,
        quantity: 1,
        total: 0
      });
    }
  };
  
  // Função para alterar a moeda do cenário
  const handleCurrencyChange = (currency: string) => {
    if (editingScenario) {
      setEditingScenario({
        ...editingScenario,
        currency
      });
    }
  };
  
  // Função para obter o símbolo da moeda
  const getCurrencySymbol = (currencyCode: string): string => {
    const currency = CURRENCIES.find(c => c.code === currencyCode);
    return currency ? currency.symbol : currencyCode;
  };
  
  // Função para formatar valor monetário de acordo com a moeda
  const formatCurrency = (value: number, currencyCode: string): string => {
    const symbol = getCurrencySymbol(currencyCode);
    
    return `${symbol} ${value.toLocaleString('pt-BR', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };
  
  // Função para calcular o total ao alterar preço unitário ou quantidade
  useEffect(() => {
    if (newItem.unitPrice !== undefined && newItem.quantity !== undefined) {
      const total = newItem.unitPrice * newItem.quantity;
      setNewItem(prev => ({...prev, total}));
    }
  }, [newItem.unitPrice, newItem.quantity]);
  
  // Função para atualizar o campo de detalhamento de preços
  const handlePriceDetailsChange = (value: string) => {
    handleChange('priceDetails', value);
  };

  // Função para atualizar o toggle de exibição da aba de preços
  const handleShowPriceTabChange = (value: boolean) => {
    handleChange('showPriceTab', value);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Preço</h2>
          <div className="flex items-center space-x-2">
            <Switch 
              id="show-price-tab"
              checked={quotation.showPriceTab === true}
              onCheckedChange={handleShowPriceTabChange}
            />
            <Label htmlFor="show-price-tab" className="flex items-center">
              <Eye className="h-4 w-4 mr-2 text-voah-teal" />
              Exibir aba de preços na visualização
            </Label>
          </div>
        </div>
        
        <div className="space-y-6">
          {/* Seção de Cenários de Preço */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-medium">Cenários de Preço</h3>
              <Button 
                onClick={addScenario}
                size="sm"
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" /> Novo Cenário
              </Button>
            </div>
            
            {scenarios.length === 0 ? (
              <Card className="border border-dashed border-gray-300">
                <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                  <Layers className="h-10 w-10 text-gray-400 mb-2" />
                  <p className="text-gray-500 mb-4">Nenhum cenário de preço adicionado</p>
                  <Button 
                    onClick={addScenario}
                    variant="outline"
                    className="flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" /> Adicionar Cenário
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Accordion type="single" collapsible className="space-y-2">
                {scenarios.map((scenario, index) => (
                  <AccordionItem key={scenario.id} value={scenario.id} className="border p-2 rounded-md">
                    <AccordionTrigger className="px-2 hover:no-underline">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center">
                          <Users className="mr-2 h-4 w-4 text-voah-teal" />
                          <span>{scenario.title}</span>
                        </div>
                        <div className="text-voah-teal font-semibold">
                          {formatCurrency(scenario.total, scenario.currency || 'BRL')}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-2">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <Button 
                            onClick={() => editScenario(scenario.id)}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-3 w-3" /> Editar
                          </Button>
                          <div className="flex items-center">
                            <CreditCard className="h-4 w-4 mr-1 text-gray-500" />
                            <span className="text-sm text-gray-500">
                              {scenario.currency || 'BRL'}
                            </span>
                          </div>
                          <Button 
                            onClick={() => deleteScenario(scenario.id)}
                            variant="destructive"
                            size="sm"
                            className="flex items-center gap-1"
                          >
                            <Trash className="h-3 w-3" /> Excluir
                          </Button>
                        </div>
                        
                        {scenario.items.length > 0 ? (
                          <div className="border rounded-md overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left">Descrição</th>
                                  <th className="px-4 py-2 text-right">Preço Unit.</th>
                                  <th className="px-4 py-2 text-right">Qtd.</th>
                                  <th className="px-4 py-2 text-right">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {scenario.items.map(item => (
                                  <tr key={item.id} className="border-t">
                                    <td className="px-4 py-2">{item.description}</td>
                                    <td className="px-4 py-2 text-right">
                                      {formatCurrency(item.unitPrice, scenario.currency || 'BRL')}
                                    </td>
                                    <td className="px-4 py-2 text-right">{item.quantity}</td>
                                    <td className="px-4 py-2 text-right">
                                      {formatCurrency(item.total, scenario.currency || 'BRL')}
                                    </td>
                                  </tr>
                                ))}
                                <tr className="border-t bg-gray-50 font-semibold">
                                  <td colSpan={3} className="px-4 py-2 text-right">Total:</td>
                                  <td className="px-4 py-2 text-right">
                                    {formatCurrency(scenario.total, scenario.currency || 'BRL')}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="p-4 border border-dashed border-gray-300 rounded-md bg-gray-50 text-center">
                            <p className="text-gray-500">Nenhum item adicionado neste cenário.</p>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
          
          {/* Editor de Texto Rico para Detalhamento */}
          <div>
            <h3 className="text-lg font-medium mb-2">Detalhamento de Preços</h3>
            <p className="text-sm text-gray-500 mb-2">
              Descreva detalhadamente os preços que serão exibidos na cotação.
            </p>
            <RichTextEditor
              value={quotation.priceDetails || ''}
              onChange={handlePriceDetailsChange}
              placeholder="Descreva os preços, condições de pagamento, etc."
              className="min-h-[200px]"
              rows={10}
            />
          </div>
        </div>
      </div>
      
      {/* Dialog para edição de cenário */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[800px] w-[90vw]">
          <DialogHeader>
            <DialogTitle>Editar Cenário</DialogTitle>
            <DialogDescription>
              Adicione ou edite itens para calcular o preço deste cenário.
            </DialogDescription>
          </DialogHeader>
          
          {editingScenario && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="scenario-title">Título</Label>
                  <Input
                    id="scenario-title"
                    value={editingScenario.title}
                    onChange={(e) => setEditingScenario({...editingScenario, title: e.target.value})}
                    placeholder="Nome do cenário"
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="scenario-currency">Moeda</Label>
                  <Select
                    value={editingScenario.currency || 'BRL'}
                    onValueChange={handleCurrencyChange}
                  >
                    <SelectTrigger id="scenario-currency" className="w-full">
                      <SelectValue placeholder="Moeda" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(currency => (
                        <SelectItem key={currency.code} value={currency.code}>
                          <div className="flex items-center">
                            <span className="mr-2">{currency.symbol}</span>
                            <span>{currency.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium">Itens do Cenário</h4>
                  <div className="text-sm text-gray-500">
                    Total: <span className="font-semibold text-voah-teal">
                      {formatCurrency(editingScenario.total, editingScenario.currency || 'BRL')}
                    </span>
                  </div>
                </div>
                
                <div className="border rounded-md overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Descrição</th>
                        <th className="px-4 py-2 text-right">Preço Unit.</th>
                        <th className="px-4 py-2 text-right">Qtd.</th>
                        <th className="px-4 py-2 text-right">Total</th>
                        <th className="px-4 py-2 text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingScenario.items.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 text-center text-gray-500">
                            Nenhum item adicionado.
                          </td>
                        </tr>
                      ) : (
                        editingScenario.items.map(item => (
                          <tr key={item.id} className="border-t">
                            <td className="px-4 py-2">{item.description}</td>
                            <td className="px-4 py-2 text-right">
                              {formatCurrency(item.unitPrice, editingScenario.currency || 'BRL')}
                            </td>
                            <td className="px-4 py-2 text-right">{item.quantity}</td>
                            <td className="px-4 py-2 text-right">
                              {formatCurrency(item.total, editingScenario.currency || 'BRL')}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <div className="flex justify-center space-x-2">
                                <Button 
                                  onClick={() => startEditingItem(item)} 
                                  variant="ghost" 
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                >
                                  <PenSquare className="h-3.5 w-3.5" />
                                </Button>
                                <Button 
                                  onClick={() => removeItemFromScenario(item.id)} 
                                  variant="ghost" 
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-md border">
                  <h4 className="text-sm font-medium mb-3">
                    {editingItem ? 'Editar Item' : 'Adicionar Novo Item'}
                  </h4>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex flex-col space-y-1">
                      <Label htmlFor="item-description" className="text-xs">Descrição</Label>
                      <Input
                        id="item-description"
                        value={newItem.description}
                        onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                        placeholder="Descrição do item"
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex flex-col space-y-1">
                        <Label htmlFor="item-price" className="text-xs">
                          Preço Unitário ({getCurrencySymbol(editingScenario.currency || 'BRL')})
                        </Label>
                        <Input
                          id="item-price"
                          type="number"
                          min="0"
                          step="0.01"
                          value={newItem.unitPrice}
                          onChange={(e) => setNewItem({...newItem, unitPrice: parseFloat(e.target.value) || 0})}
                          placeholder="0,00"
                        />
                      </div>
                      
                      <div className="flex flex-col space-y-1">
                        <Label htmlFor="item-quantity" className="text-xs">Quantidade</Label>
                        <Input
                          id="item-quantity"
                          type="number"
                          min="1"
                          value={newItem.quantity}
                          onChange={(e) => setNewItem({...newItem, quantity: parseInt(e.target.value) || 1})}
                          placeholder="1"
                        />
                      </div>
                      
                      <div className="flex flex-col space-y-1">
                        <Label htmlFor="item-total" className="text-xs">
                          Total ({getCurrencySymbol(editingScenario.currency || 'BRL')})
                        </Label>
                        <Input
                          id="item-total"
                          type="number"
                          disabled
                          value={newItem.total}
                          className="bg-gray-100"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      {editingItem ? (
                        <div className="flex space-x-2">
                          <Button 
                            onClick={() => {
                              setEditingItem(null);
                              setNewItem({
                                description: '',
                                unitPrice: 0,
                                quantity: 1,
                                total: 0
                              });
                            }}
                            variant="outline"
                            size="sm"
                          >
                            Cancelar
                          </Button>
                          <Button 
                            onClick={saveItemEdit}
                            variant="default"
                            size="sm"
                            disabled={!newItem.description}
                          >
                            <Save className="h-3.5 w-3.5 mr-1" /> Salvar Alterações
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          onClick={addItemToScenario}
                          variant="default"
                          size="sm"
                          disabled={!newItem.description}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar Item
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setIsDialogOpen(false)} variant="outline">
              Cancelar
            </Button>
            <Button onClick={saveScenario} disabled={!editingScenario}>
              Salvar Cenário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 